<template>
  <div>
    <div class="d-flex align-items-center">
      <a-icon style="font-size: 20px; cursor: pointer;" class="mr-2" type="arrow-left" @click="$router.back()" />
      <h1 class="h3 mb-0">
        {{ $t('inventory.goods_receipt.detail_gr') }}: {{ $route.query.number_gr }}
      </h1>
    </div>
    <div class="mt-4">
      <a-table
        class="gr-list"
        :data-source="products"
        :row-key="record => record.catalog_id"
        :columns="columns"
        :loading="isLoading"
        :scroll="{ y: 360, x: 1100 }"
        :pagination="{
          showTotal: (total, range) => $t('utils.pagination_show_total', { perpage: `${range[0]} - ${range[1]}`, total }),
          total: total_row,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          locale: { items_per_page: $t('utils.items_per_page') }
        }"
        bordered
      >
        <template slot="numberColumn" slot-scope="text, record, index">
          {{ index + 1 }}
        </template>
        <template slot="stock_type" slot-scope="text">
          {{ text || '-' }}
        </template>
      </a-table>
    </div>
  </div>
</template>

<script>
import { getDetailGoodsReceipt, getDetailGoodsReceiptByInbound } from '@/api/good-receive'
import { getDetailPOByNumber } from '@/api/purchase-order'
import { getUomBySku } from '@/api/inventory'

export default {
  data() {
    return {
      screenHeight: ((screen.height - 900) * 0.5) + (screen.height * 0.5),
      total_row: 0,
      isLoading: false,
      products: [],
      stockList: {
        AV: {
          id: 'AV',
          label: 'Available',
        },
        QR: {
          id: 'QR',
          label: 'Quarantine',
        },
        DMG: {
          id: 'DMG',
          label: 'Damage',
        },
        WIP: {
          id: 'WIP',
          label: 'Work In Progress',
        },
      },
    }
  },
  computed: {
    columns() {
      return [
        {
          title: this.$t('inventory.goods_receipt.columns.no'),
          dataIndex: 'number',
          key: 'number',
          width: 60,
        },
        {
          title: this.$t('inventory.goods_receipt.columns.product_name'),
          dataIndex: 'catalog_title',
          key: 'catalog_title',
          width: 300,
        },
        {
          title: this.$t('inventory.goods_receipt.columns.sku'),
          dataIndex: 'sku',
          key: 'sku',
          width: 150,
        },
        {
          title: this.$t('inventory.goods_receipt.batch_no'),
          dataIndex: 'batch_number',
          key: 'batch_number',
          width: 150,
        },
        {
          title: this.$t('inventory.goods_receipt.expired_date'),
          dataIndex: 'expired_date',
          key: 'expired_date',
          width: 150,
        },
        {
          title: this.$t('inventory.goods_receipt.reference_detail'),
          children: [
            {
              title: this.$t('inventory.goods_receipt.qty_inbound'),
              dataIndex: 'qty_inbound',
              key: 'qty_inbound',
              width: 120,
            },
            {
              title: this.$t('inventory.goods_receipt.uom_inbound'),
              dataIndex: 'uom_inbound',
              key: 'uom_inbound',
              width: 100,
            },
            {
              title: this.$t('inventory.goods_receipt.qty_ref_max'),
              dataIndex: 'qty_max',
              key: 'qty_max',
              width: 120,
            },
            {
              title: this.$t('inventory.goods_receipt.uom_ref_max'),
              dataIndex: 'uom_max',
              key: 'uom_max',
              width: 140,
            },
          ],
        },
        {
          title: this.$t('inventory.goods_receipt.gr_detail'),
          children: [
            {
              title: this.$t('inventory.goods_receipt.qty_gr'),
              dataIndex: 'qty_gr',
              key: 'qty_gr',
              width: 120,
            },
            {
              title: this.$t('inventory.goods_receipt.uom_gr'),
              dataIndex: 'uom_gr',
              key: 'uom_gr',
              width: 100,
            },
            {
              title: this.$t('po.stock_type'),
              dataIndex: 'stock_type',
              key: 'stock_type',
              width: 150,
              scopedSlots: {
                customRender: 'stock_type',
              },
            },
            {
              title: this.$t('inventory.goods_receipt.qty_outstanding'),
              dataIndex: 'qty_outstanding',
              key: 'qty_outstanding',
              width: 150,
            },
            {
              title: this.$t('inventory.goods_receipt.uom_outstanding'),
              dataIndex: 'uom_outstanding',
              key: 'uom_outstanding',
              width: 160,
            },
          ],
        },
        {
          title: this.$t('inventory.goods_receipt.base_info'),
          children: [
            {
              title: this.$t('inventory.goods_receipt.base_qty'),
              dataIndex: 'base_quantity',
              key: 'base_quantity',
              width: 100,
            },
            {
              title: this.$t('inventory.goods_receipt.base_uom'),
              dataIndex: 'base_uom',
              key: 'base_uom',
              width: 110,
            },
          ],
        },
      ]
    },
    businessId() {
      if (this.$store.state.user.restriction_base === 'Warehouse') {
        return Object.keys(this.$store.getters['user/businessListObjectByKey'])[0] || ''
      }
      return this.$route.query.business_id || ''
    },
    scroll() {
      let yValue = 360
      if (this.screenHeight > 450) {
        yValue = this.screenHeight
      }
      
      return {
        y: yValue,
        x: 1000,
      }
    },
    tHeight() {
      if (!this.products.length) {
        return '0px'
      }
      
      if (this.screenHeight > 450) {
        return `${this.screenHeight}px`
      }
      
      return '450px'
    },
  },
  watch: {
    businessId() {
      this.getDetailGR()
    },
  },
  created() {
    this.init()
  },
  methods: {
    async init() {
      this.isLoading = true
      const promises = []
      if (!this.$route.query.number_inbound) {
        promises.push(
          getDetailGoodsReceipt({
            business_id: this.businessId,
            id: this.$route.params.id,
          }),
        )
      }

      if (this.$route.query.number_inbound) {
        promises.push(
          getDetailGoodsReceiptByInbound({
            business_id: this.businessId,
            number: this.$route.query.number_inbound,
          }),
        )
        promises.push(
          getDetailPOByNumber({
            business_id: this.businessId,
            number: this.$route.query.number_inbound,
          }),
        )
      }

      await Promise.all(promises)
        .then(async res => {
          let response = res?.[0]?.data || []
          if (this.$route.query.number_inbound && response?.length) {
            let grIndex = -1
            let total = {}
            let goodReceiveId = null
            const grItem = []
            const countItem = response?.filter(item => {
              goodReceiveId = !goodReceiveId ? item.good_receive_id : goodReceiveId
              return item.good_receive_id === goodReceiveId
            })?.length || 0
            response?.forEach((item, index) => {
              if (grIndex < 0 || (grIndex > -1 && !grItem?.find(gr => gr.catalog_id === item.catalog_id))) {
                if (!total?.[item.catalog_id]) {
                  total[item.catalog_id] = +item.quantity
                } else {
                  total[item.catalog_id] += +item.quantity
                }
              }

              if (item.good_receive_id === this.$route.params.id) {
                grItem.push(item)
                if (grIndex < 0 && ((index + 1) % countItem == 0)) {
                  grIndex = index
                }
              }
            })
            
            if (grIndex > 0) {
              for (const [key, value] of Object.entries(total)) {
                const itemIndex = grItem.findIndex(i => i.catalog_id === key)
                if (itemIndex > -1) {
                  grItem[itemIndex].qty_total = value
                }
              }
            }

            response = grItem
          }

          this.products = response?.length
            ? await Promise.all(response?.map(async (item, index) => {
                let qty_inbound = null
                let uom_inbound = null
                let qty_max = null
                if (this.$route.query.number_inbound && res?.[1]?.data) {
                  const resp = res?.[1]?.data || []
                  const findDetail = resp.find(detail => detail.catalog_id === item.catalog_id)
                  if (findDetail?.sku) {
                    const productUom = await this.getProductUom(item.sku)
                    const findUom = productUom?.find(product => product.unit === findDetail.uom)
                    qty_inbound = +findDetail.qty
                    uom_inbound = findDetail.uom
                    qty_max = +findDetail.qty * (+findUom?.numerator || 1)
                  }
                }

                return {
                  ...item,
                  number: item?.number || index + 1,
                  batch_number: item.batch_number || '-',
                  expired_date: item?.expired_date
                    ? this.$moment(item.expired_date).format('DD MMM YYYY')
                    : '-',
                  qty_inbound: qty_inbound || '-',
                  uom_inbound: uom_inbound || '-',
                  qty_max: qty_max
                    ? +item.quantity + ((+qty_max || 0) - (+item.qty_total || +item.quantity || 0))
                    : item.quantity,
                  uom_max: item.base_uom,
                  qty_gr: +item.quantity,
                  uom_gr: item.uom,
                  qty_outstanding: qty_max
                    ? (+qty_max || 0) - (+item.qty_total || +item.quantity || 0)
                    : '-',
                  uom_outstanding: item.base_uom,
                  stock_type: item?.stock_type && this.stockList?.[item?.stock_type?.toUpperCase()]?.label
                    ? this.stockList[item.stock_type.toUpperCase()].label
                    : item?.stock_type,
                  base_quantity: qty_max || '-',
                }
              }))
            : []
          this.total_row = response?.length || 0
        })
        .catch(err => {
          this.total_row = 0
          this.products = []
          console.error(err)
        })
        .finally(() => this.isLoading = false)
    },
    async getProductUom(sku) {
      return await getUomBySku({
        sku,
        params: { business_id: this.businessId },
      })
      .then(({ data: { data: response } }) => response?.units || [])
      .catch(() => [])
    },
  },
}
</script>

<style lang="scss">
.select-antd-default.ant-select-lg {
  width: 100%;

  .ant-select-selection__rendered {
    line-height: 38px !important;
  }
}

.gr-list div.ant-table-body {
  min-height: v-bind(tHeight);
}
</style>